import { Alert, Button, Card, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Cover = {
  id: string;
  status: string;
  period_code?: string;
  period_name?: string;
  subject_code?: string;
  subject_name?: string;
  section_name?: string;
  grade_code?: string;
  room_code?: string;
  cover_room_code?: string;
  cover_first_name?: string;
  cover_last_name?: string;
  cover_employee_code?: string;
  notified_at?: string | null;
};

type Detail = {
  day: {
    id: string;
    status: string;
    absence_date: string;
    session_name?: string;
    absent_first_name?: string;
    absent_last_name?: string;
    absent_employee_code?: string;
    reason?: string | null;
    hr_leave_application_id?: string | null;
  };
  covers: Cover[];
};

export default function SubstitutionDayDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.substitution.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const detail = useQuery({
    queryKey: ['education-substitution-day', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<Detail>(`/education/substitutions/${id}`);
      return res.data;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-substitution-day', id] });
    queryClient.invalidateQueries({ queryKey: ['education-substitutions'] });
  };

  const refresh = useMutation({
    mutationFn: () => api.post(`/education/substitutions/${id}/refresh`),
    onSuccess: () => {
      message.success(t('substitutionRefreshed'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const closeDay = useMutation({
    mutationFn: () => api.post(`/education/substitutions/${id}/close`),
    onSuccess: () => {
      message.success(t('substitutionClosed'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const cancelDay = useMutation({
    mutationFn: () => api.post(`/education/substitutions/${id}/cancel`),
    onSuccess: () => {
      message.success(t('substitutionCancelled'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const clearCover = useMutation({
    mutationFn: (coverId: string) => api.post(`/education/substitutions/${id}/covers/${coverId}/clear`),
    onSuccess: () => {
      message.success(t('coverCleared'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (detail.isError) {
    return (
      <EducationPageChrome title={t('substitutions')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  const day = detail.data?.day;
  const locked = day?.status === 'closed' || day?.status === 'cancelled';
  const absentName = day
    ? `${day.absent_employee_code || ''} — ${[day.absent_first_name, day.absent_last_name].filter(Boolean).join(' ')}`.trim()
    : '';

  return (
    <EducationPageChrome
      title={day ? `${t('substitutionDay')}: ${String(day.absence_date).slice(0, 10)}` : t('substitutions')}
      description={
        day
          ? `${absentName} · ${day.session_name || ''} · ${day.status}`
          : t('substitutionsIntro')
      }
      extra={
        <Space wrap>
          {canManage && !locked ? (
            <>
              <Button loading={refresh.isPending} onClick={() => refresh.mutate()}>
                {t('refreshCovers')}
              </Button>
              <Popconfirm title={t('closeSubstitutionConfirm')} onConfirm={() => closeDay.mutate()}>
                <Button loading={closeDay.isPending}>{t('closeSubstitution')}</Button>
              </Popconfirm>
              <Popconfirm title={t('cancelSubstitutionConfirm')} onConfirm={() => cancelDay.mutate()}>
                <Button danger loading={cancelDay.isPending}>
                  {t('cancelSubstitution')}
                </Button>
              </Popconfirm>
            </>
          ) : null}
          <Button onClick={() => navigate('/education/substitutions')}>{t('backToSubstitutions')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {locked ? <Alert type="info" showIcon message={t('substitutionDayLocked')} /> : null}
        {day?.reason ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('reason')}: {day.reason}
          </Typography.Paragraph>
        ) : null}
        {!detail.isLoading && (detail.data?.covers?.length ?? 0) === 0 ? (
          <Alert type="warning" showIcon message={t('substitutionNoCovers')} />
        ) : null}
        <Card size="small" loading={detail.isLoading}>
          <Table
            rowKey="id"
            size="small"
            dataSource={detail.data?.covers ?? []}
            pagination={false}
            columns={[
              {
                title: t('period'),
                render: (_: unknown, row: Cover) => `${row.period_code || ''} — ${row.period_name || ''}`,
              },
              {
                title: t('section'),
                render: (_: unknown, row: Cover) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || ''}`,
              },
              {
                title: t('subject'),
                render: (_: unknown, row: Cover) => `${row.subject_code || ''} — ${row.subject_name || ''}`,
              },
              {
                title: t('room'),
                render: (_: unknown, row: Cover) => row.cover_room_code || row.room_code || '—',
              },
              {
                title: t('coverTeacher'),
                render: (_: unknown, row: Cover) => {
                  if (!row.cover_employee_code && !row.cover_first_name) return '—';
                  return `${row.cover_employee_code || ''} — ${[row.cover_first_name, row.cover_last_name]
                    .filter(Boolean)
                    .join(' ')}`.trim();
                },
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string, row: Cover) => (
                  <Space size={4}>
                    <Tag color={v === 'assigned' ? 'success' : v === 'cancelled' ? 'default' : 'warning'}>
                      {v}
                    </Tag>
                    {row.notified_at ? <Tag>{t('notified')}</Tag> : null}
                  </Space>
                ),
              },
              ...(canManage && !locked
                ? [
                    {
                      title: t('actions'),
                      width: 180,
                      render: (_: unknown, row: Cover) =>
                        row.status === 'cancelled' ? null : (
                          <Space size="small">
                            <Button
                              size="small"
                              type="link"
                              onClick={() =>
                                navigate(`/education/substitutions/${id}/covers/${row.id}/assign`)
                              }
                            >
                              {row.status === 'assigned' ? t('reassignCover') : t('assignCover')}
                            </Button>
                            {row.status === 'assigned' ? (
                              <Popconfirm
                                title={t('clearCoverConfirm')}
                                onConfirm={() => clearCover.mutate(row.id)}
                              >
                                <Button size="small" type="link" danger>
                                  {t('clear')}
                                </Button>
                              </Popconfirm>
                            ) : null}
                          </Space>
                        ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
